import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/index.css';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import App from './App.tsx';
import { TechnicalErrorFallback } from './components/TechnicalErrorFallback';
import { initAnalytics } from './lib/analytics/posthog';
import { captureFbclidOnEntry } from './lib/analytics/meta';
import { captureUtmsFromUrl } from './lib/utm';
import { useAuthStore } from './stores/authStore';

// Cross #10 Cluster Y1 — Sentry init runs synchronously before React
// mounts so the ErrorBoundary's first render already has Sentry available
// for capture. Privacy beforeSend hook strips body, query string, cookies,
// headers from every event AND walks breadcrumbs to strip query params
// from any captured URL — Stripe success/password-reset/email-verify URLs
// all carry tokens or session_ids in query strings.
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.PROD && !!import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
  tracesSampleRate: 0,
  sendDefaultPii: false,
  integrations: [
    // Routing instrumentation stays installed but dormant while
    // tracesSampleRate is 0; auto-activates the day sampling turns on.
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  beforeSend(event) {
    if (event.request) {
      delete event.request.data;
      delete event.request.query_string;
      if (event.request.url) {
        try {
          const u = new URL(event.request.url);
          event.request.url = `${u.origin}${u.pathname}`;
        } catch {
          // URL parse failure — leave url as-is
        }
      }
      delete event.request.cookies;
      delete event.request.headers;
    }
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((bc) => {
        if (bc.data && typeof bc.data.url === 'string') {
          try {
            const u = new URL(bc.data.url);
            bc.data = { ...bc.data, url: `${u.origin}${u.pathname}` };
          } catch {
            // URL parse failure — leave bc.data.url as-is
          }
        }
        return bc;
      });
    }
    return event;
  },
});

// Marketing attribution package (2026-07-17). Capture UTMs from the URL
// into sessionStorage BEFORE initAnalytics(): both this and PostHog's
// in-memory `pendingUtms` read from `window.location.search`, and running
// this first keeps them seeded from the same URL parse even if PostHog's
// consent-gate init happens later in the session.
captureUtmsFromUrl();

// Meta CAPI attribution (2026-10-29). Capture raw `fbclid` from the
// landing URL into sessionStorage. The value is session-scoped and only
// forwarded to the backend at checkout time when marketing consent is
// granted (gate lives in BestellungReviewScreen). This is independent
// of PostHog's boolean-only `has_fbclid` super-property capture.
captureFbclidOnEntry();

// PostHog analytics — consent-gated, opted-out until Cookiebot statistics
// consent. No-op when VITE_POSTHOG_API_KEY is absent (dev/preview).
// Internal-user tagging (Sebastian ruling 2026-10-08): the DI closure
// returns id + email so posthog.ts can mark @dreamable.kids users with
// `is_internal: true` on identify.
initAnalytics(() => {
  const user = useAuthStore.getState().user;
  return user ? { id: user.id, email: user.email } : undefined;
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<TechnicalErrorFallback />}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then(() => console.log('Service worker registered'))
      .catch((err) => console.error('Service worker registration failed:', err));
  });
}
